import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppId, AppThunkAPIType } from "../../app/appTypes";
import { getWithAuth, postWithAuth } from "../../http";

export const CLEANLINESS_CLASSES = ["A", "B", "C", "D"] as const;
export type CleanlinessClassGmp = typeof CLEANLINESS_CLASSES[number];

export const AREA_UNITS = ["SQUARE_METER", "SQUARE_FOOT"] as const;
export type AreaUnit = typeof AREA_UNITS[number];

export const VOLUME_UNITS = ["CUBIC_METER", "CUBIC_FOOT", "LITER"] as const;
export type VolumeUnit = typeof VOLUME_UNITS[number];


export interface Room {
    id: number
    code: string
    name: string
    cleanlinessClass: CleanlinessClassGmp
    roomArea: number
    roomAreaUnit: AreaUnit
    cubicCapacity: number
    cubicCapacityUnit: VolumeUnit
    structureId: AppId | null
    companyId: number
    roomGroupId: number | null
    createdBy: string
    createdByFullName: string
    createDate: Date
}

export interface RoomGroup {
    id: number
    companyId: number
    name: string
    description?: string | null
    structureId: AppId
    createdBy: string
    createdByFullName: string
    createDate: Date
    rooms: Room[]
}



export type CreateRoomGroupPayload = Omit<RoomGroup, "id" | "rooms" | "createdBy" | "createdByFullName" | "createDate"> & {
    description?: string | null;
    structureId?: AppId | null;
    rooms: NewRoomPayload[];
    createdBy: string;
    createdByFullName: string;
    createDate: Date;
};

export type NewRoomPayload = {
    code: string;
    name: string;
    cleanlinessClass: CleanlinessClassGmp;
    roomArea: number;
    roomAreaUnit: AreaUnit;
    cubicCapacity: number;
    cubicCapacityUnit: VolumeUnit;
    structureId?: AppId | null;
};


export const loadRoomGroups = createAsyncThunk<
    RoomGroup[],
    void,
    AppThunkAPIType
>(
    "roomGroups/loadAll",
    async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/room-groups` }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as RoomGroup[];
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const loadRoomGroup = createAsyncThunk<
    RoomGroup,
    number,
    AppThunkAPIType
>(
    "room-groups/loadOne",
    async (groupId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/room-groups/${groupId}` }));
        if (getWithAuth.fulfilled.match(result)) {
            return result.payload;
        }
        return rejectWithValue(result.payload ?? { kind: "unknown" });
    }
);

export const createRoomGroup = createAsyncThunk<
    RoomGroup,
    CreateRoomGroupPayload,
    AppThunkAPIType
>(
    "room-groups/create",
    async (payload, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: "api/room-groups",
            payload
        }));
        const { payload: data } = result;

        if (postWithAuth.fulfilled.match(result)) {
            return data as RoomGroup;
        } else {
            return rejectWithValue(data ?? { kind: "unknown" });
        }
    }
);